import { SpotLabelPatternItem } from '../types';

function isProgressive(item: SpotLabelPatternItem): boolean {
  return (
    item.type === 'numbers' || item.type === 'letters' || item.type === 'array'
  );
}

function getInitialValue(item: SpotLabelPatternItem): string {
  switch (item.type) {
    case 'numbers':
      return '1';
    case 'letters':
      return 'A';
    case 'array':
      return item.value.length > 0 ? item.value[0] : '';
    case 'text':
      return item.value; // Fixed text always remains the same
  }
}

function isAtMax(item: SpotLabelPatternItem, currentValue: string) {
  if (!isProgressive(item)) return true; // Fixed text is always "complete"

  switch (item.type) {
    case 'numbers':
      return Number.parseInt(currentValue, 10) >= item.depth;
    case 'letters': {
      const maxLetter = String.fromCharCode('A'.charCodeAt(0) + item.depth - 1);
      return currentValue === maxLetter;
    }
    case 'array':
      return item.value.indexOf(currentValue) === item.value.length - 1;
  }

  return false;
}

function getNextValue(item: SpotLabelPatternItem, currentValue: string) {
  if (!isProgressive(item)) return currentValue; // Ensure only progressive items change

  switch (item.type) {
    case 'numbers':
      return (Number.parseInt(currentValue, 10) + 1).toString();
    case 'letters':
      return String.fromCharCode(currentValue.charCodeAt(0) + 1);
    case 'array': {
      const idx = item.value.indexOf(currentValue);
      return idx >= 0 && idx < item.value.length - 1
        ? item.value[idx + 1]
        : item.value[0];
    }
  }

  return;
}

export function generateNextSpotLabels(
  pattern: SpotLabelPatternItem[],
  currentParts: string[]
): string[] | null {
  // Check if `currentParts` is valid, otherwise reset to default
  if (
    currentParts.length !== pattern.length ||
    !currentParts.every((part, index) => {
      const item = pattern[index];

      if (item.type === 'text') return part === item.value; // Ensure text matches exactly
      if (item.type === 'numbers') return !isNaN(Number.parseInt(part, 10)); // Ensure numbers are valid
      if (item.type === 'letters')
        return part.length === 1 && part >= 'A' && part <= 'Z'; // Ensure single letter
      if (item.type === 'array') return item.value.includes(part); // Ensure it's a valid option from the array

      return false; // If anything doesn't match, reset
    })
  ) {
    // If anything is invalid, reset from the beginning
    return pattern.map(getInitialValue);
  }

  const nextParts = [...currentParts];

  for (let i = pattern.length - 1; i >= 0; i--) {
    const item = pattern[i];

    if (isProgressive(item)) {
      if (!isAtMax(item, currentParts[i])) {
        nextParts[i] = getNextValue(item, currentParts[i]) || '';

        // Reset everything to the right of this index
        for (let j = i + 1; j < pattern.length; j++) {
          if (isProgressive(pattern[j])) {
            nextParts[j] = getInitialValue(pattern[j]);
          }
        }
        return nextParts;
      }
    }
  }
  return null; // No further progression possible
}
